import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { BedDouble, Building2, CalendarDays, CheckCircle2, ExternalLink, Loader2, Search, ShieldCheck, WalletCards } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COUNTRIES = [
  ["DE", "Deutschland"], ["AT", "Österreich"], ["CH", "Schweiz"], ["FR", "Frankreich"],
  ["IT", "Italien"], ["NL", "Niederlande"], ["ES", "Spanien"], ["GB", "Großbritannien"],
];

const isoToday = () => new Date().toISOString().slice(0, 10);
const tomorrow = () => {
  const date = new Date();
  date.setDate(date.getDate() + 1);
  return date.toISOString().slice(0, 10);
};

const formatMoney = (amount, currency) => {
  if (amount === undefined || amount === null || amount === "") return "Preis auf Anfrage";
  try { return new Intl.NumberFormat("de-DE", { style: "currency", currency: currency || "EUR" }).format(Number(amount)); }
  catch { return `${amount} ${currency || ""}`.trim(); }
};

export const LiteApiAccommodationSearch = ({ trip, editable, onAddCost }) => {
  const [status, setStatus] = useState({ configured: false, message: "LiteAPI-Sandbox wird geprüft …" });
  const [form, setForm] = useState({
    city_name: trip?.ziel || "", country_code: "DE", checkin: trip?.start_datum || tomorrow(),
    checkout: trip?.end_datum || "", adults: 1, currency: trip?.currency || "EUR", guest_nationality: "DE",
  });
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [checking, setChecking] = useState("");
  const [prebook, setPrebook] = useState(null);

  useEffect(() => {
    axios.get(`${API}/reise/liteapi/status`).then((res) => setStatus(res.data || {})).catch(() => setStatus({ configured: false, message: "LiteAPI-Sandbox ist derzeit nicht erreichbar." }));
  }, []);

  useEffect(() => {
    setForm((current) => ({
      ...current,
      city_name: current.city_name || trip?.ziel || "",
      checkin: trip?.start_datum || current.checkin,
      checkout: trip?.end_datum || current.checkout,
      currency: trip?.currency || current.currency,
    }));
  }, [trip?.ziel, trip?.start_datum, trip?.end_datum, trip?.currency]);

  const isCustomerProduction = status.mode === "customer_production";
  const canSearch = useMemo(() => status.configured && editable && form.city_name.trim() && form.country_code && form.checkin && form.checkout && form.checkout > form.checkin, [status.configured, editable, form]);

  const update = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  const search = async () => {
    if (!canSearch) {
      toast.error("Bitte Ziel, Land sowie gültige An- und Abreisedaten angeben.");
      return;
    }
    setSearching(true); setResults([]); setSelected(null); setPrebook(null);
    try {
      const response = await axios.post(`${API}/reise/liteapi/search`, { ...form, adults: Number(form.adults) || 1, limit: 20 });
      setResults(response.data?.results || []);
      toast.success(`${response.data?.results?.length || 0} Unterkünfte gefunden`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unterkunftssuche fehlgeschlagen");
    } finally { setSearching(false); }
  };

  const checkPrice = async (offer) => {
    if (!offer.offer_id) { toast.error("Dieses Angebot enthält keine prüfbare LiteAPI-Angebotskennung."); return; }
    setSelected(offer); setChecking(offer.offer_id); setPrebook(null);
    try {
      const response = await axios.post(`${API}/reise/liteapi/prebook`, { offer_id: offer.offer_id });
      setPrebook({ ...response.data, offer });
      toast.success("Preis und Verfügbarkeit wurden in der Sandbox geprüft");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Preisprüfung fehlgeschlagen");
    } finally { setChecking(""); }
  };

  const addAsCost = () => {
    if (!prebook?.offer || !onAddCost) return;
    const amount = prebook?.price?.total?.[0]?.amount ?? prebook?.price?.amount ?? prebook.offer.amount;
    if (amount === undefined || amount === null || amount === "") { toast.error("Für dieses Angebot ist kein übernehmbarer Preis vorhanden."); return; }
    onAddCost({
      beschreibung: `Unterkunft (Sandbox vorgemerkt): ${prebook.offer.name}${prebook.offer.room_name ? ` – ${prebook.offer.room_name}` : ""}`,
      betrag: Number(amount),
      liteapi_prebook_id: prebook.prebook_id,
      liteapi_mode: "sandbox",
    });
    toast.success("Unterkunftskosten als unverbindliche Reise-Kostenposition vorgemerkt");
  };

  return (
    <Card className="p-5 border-[var(--brand-border)] space-y-4" data-testid="liteapi-accommodation-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-semibold text-[var(--brand-ink)] flex items-center gap-2"><BedDouble className="w-4 h-4" /> Unterkünfte</h3>
          <p className="text-xs text-[var(--brand-muted)] mt-1">Hotels, Apartments und Ferienwohnungen über LiteAPI / Nuitee Connect.</p>
        </div>
        <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-800 px-2.5 py-1 text-xs font-medium border border-amber-200"><ShieldCheck className="w-3.5 h-3.5" /> {isCustomerProduction ? "Eigener Nuitee-Zugang · Zahlung gesperrt" : "Sandbox · keine reale Zahlung"}</span>
      </div>

      {!status.configured ? (
        <div className="rounded-lg bg-[var(--brand-bg)] border border-[var(--brand-border)] px-4 py-3 text-sm text-[var(--brand-muted)]" data-testid="liteapi-not-configured">
          {status.message || "Die serverseitige LiteAPI-Sandbox wird vorbereitet. Es sind keine Produktionszugänge oder Zahlungsdaten hinterlegt."}
        </div>
      ) : (
        <>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="lg:col-span-2"><Label>Ort</Label><Input value={form.city_name} disabled={!editable} onChange={(event) => update("city_name", event.target.value)} placeholder="z. B. Zürich" data-testid="liteapi-city" /></div>
            <div><Label>Land</Label><select className="w-full h-10 rounded-md border border-[var(--brand-border)] bg-white px-3 text-sm" disabled={!editable} value={form.country_code} onChange={(event) => update("country_code", event.target.value)} data-testid="liteapi-country">{COUNTRIES.map(([code, label]) => <option key={code} value={code}>{label}</option>)}</select></div>
            <div><Label>Erwachsene</Label><Input type="number" min="1" max="9" value={form.adults} disabled={!editable} onChange={(event) => update("adults", event.target.value)} data-testid="liteapi-adults" /></div>
            <div><Label>Anreise</Label><Input type="date" min={isoToday()} value={form.checkin} disabled={!editable} onChange={(event) => update("checkin", event.target.value)} data-testid="liteapi-checkin" /></div>
            <div><Label>Abreise</Label><Input type="date" min={form.checkin || isoToday()} value={form.checkout} disabled={!editable} onChange={(event) => update("checkout", event.target.value)} data-testid="liteapi-checkout" /></div>
            <div><Label>Währung</Label><select className="w-full h-10 rounded-md border border-[var(--brand-border)] bg-white px-3 text-sm" disabled={!editable} value={form.currency} onChange={(event) => update("currency", event.target.value)} data-testid="liteapi-currency"><option value="EUR">EUR (€)</option><option value="CHF">CHF</option><option value="USD">USD ($)</option><option value="GBP">GBP (£)</option></select></div>
            <div><Label>Nationalität</Label><select className="w-full h-10 rounded-md border border-[var(--brand-border)] bg-white px-3 text-sm" disabled={!editable} value={form.guest_nationality} onChange={(event) => update("guest_nationality", event.target.value)} data-testid="liteapi-nationality">{COUNTRIES.map(([code, label]) => <option key={code} value={code}>{label}</option>)}</select></div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Button onClick={search} disabled={!canSearch || searching} className="bg-[var(--brand-ink)] hover:bg-black text-white" data-testid="liteapi-search">
              {searching ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Search className="w-4 h-4 mr-1.5" />} Unterkünfte suchen
            </Button>
            <span className="text-xs text-[var(--brand-muted)]">{isCustomerProduction ? "Suche über den eigenen Nuitee-Zugang. Zahlung und Buchung bleiben bis zum User-Payment-Liveprozess gesperrt." : "Unverbindliche Sandbox-Suche. Vor einer späteren Buchung werden Preis und Verfügbarkeit erneut geprüft."}</span>
          </div>

          {results.length > 0 && <div className="space-y-2" data-testid="liteapi-results">
            <div className="text-sm font-medium text-[var(--brand-ink)]">Verfügbare Unterkünfte ({results.length})</div>
            {results.map((offer, index) => (
              <div key={`${offer.offer_id}-${index}`} className={`border rounded-lg p-3 ${selected?.offer_id === offer.offer_id ? "border-[var(--brand-primary)]" : "border-[var(--brand-border)]"}`}>
                <div className="flex justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="font-medium text-sm text-[var(--brand-ink)] flex gap-1.5 items-center"><Building2 className="w-4 h-4 shrink-0" /> {offer.name}</div>
                    <div className="text-xs text-[var(--brand-muted)] mt-1">{[offer.accommodation_type, offer.address, offer.room_name, offer.board_name].filter(Boolean).join(" · ") || "Unterkunft"}</div>
                    {offer.cancellation_policy && <div className="text-xs text-[var(--brand-muted)] mt-1 line-clamp-2">Stornierung: {offer.cancellation_policy}</div>}
                  </div>
                  <div className="text-right shrink-0"><div className="font-semibold">{formatMoney(offer.amount, offer.currency || form.currency)}</div><div className="text-xs text-[var(--brand-muted)]">Gesamtpreis laut Angebot</div></div>
                </div>
                <div className="mt-3 flex gap-2 flex-wrap">
                  <Button size="sm" variant="outline" disabled={checking === offer.offer_id || !editable || !status.prebook_enabled} onClick={() => checkPrice(offer)} className="border-[var(--brand-border)]" data-testid={`liteapi-prebook-${index}`}>
                    {checking === offer.offer_id ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <CalendarDays className="w-3.5 h-3.5 mr-1" />} {isCustomerProduction ? "User Payment wird vorbereitet" : "Preis & Verfügbarkeit prüfen"}
                  </Button>
                  {offer.refundable && <span className="text-xs text-emerald-700 self-center">Stornierbar laut Angebot</span>}
                </div>
              </div>
            ))}
          </div>}

          {prebook && <div className="border border-emerald-200 bg-emerald-50 rounded-lg px-4 py-3 space-y-2" data-testid="liteapi-prebook-result">
            <div className="font-medium text-emerald-900 flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4" /> Preis und Verfügbarkeit geprüft</div>
            <div className="text-sm text-emerald-900">{prebook.offer.name} · {formatMoney(prebook?.price?.total?.[0]?.amount ?? prebook?.price?.amount ?? prebook.offer.amount, prebook.currency || prebook.offer.currency || form.currency)}</div>
            <p className="text-xs text-emerald-800">Es wurde keine Buchung und keine Zahlung ausgelöst. Eine echte Buchung bleibt bis zum späteren User-Payment-Live-Prozess gesperrt.</p>
            <div className="flex gap-2 flex-wrap"><Button size="sm" onClick={addAsCost} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] text-white" data-testid="liteapi-add-cost"><WalletCards className="w-3.5 h-3.5 mr-1" /> Als Kosten vormerken</Button><Button size="sm" variant="outline" className="border-emerald-300" onClick={() => toast.info("Die Testbuchung ist absichtlich gesperrt. Im Live-Betrieb folgt die Zahlung direkt durch den Endkunden über User Payment.")}><ExternalLink className="w-3.5 h-3.5 mr-1" /> Buchung vorbereitet</Button></div>
          </div>}
        </>
      )}
    </Card>
  );
};
